import { useCallback } from 'react'
import { ContentItemViewNodesType } from '../../../types'
import { useMOPTDialogActions } from '../store'
import { isMOPTArticle, validateMOPTPlacement } from '../utils/moptValidators'

/**
 * Return type for the useMOPTDragAndDropValidation hook
 */
export interface UseMOPTDragAndDropValidationReturn {
    /**
     * Checks if node is MOPT article and should prevent dragging
     * Automatically opens dialog if MOPT detected
     * @param node - The node to check
     * @returns true if drag should be prevented (is MOPT), false otherwise
     *
     * @example
     * if (shouldPreventDrag(dragNode)) {
     *   event.preventDefault()
     *   return
     * }
     */
    shouldPreventDrag: (node: Partial<ContentItemViewNodesType> | undefined) => boolean

    /**
     * Validates drop placement for MOPT restrictions
     * Automatically opens dialog on validation failure
     * @param dragNode - The node being moved
     * @param reorderedSiblings - The sibling array after reordering
     * @returns true if placement is valid, false otherwise
     *
     * @example
     * if (!validateDropPlacement(dragNode, reorderedSiblings)) {
     *   return // Validation failed, dialog shown
     * }
     */
    validateDropPlacement: (
        dragNode: Partial<ContentItemViewNodesType>,
        reorderedSiblings: Partial<ContentItemViewNodesType>[]
    ) => boolean

    /**
     * Simulates reorder and validates MOPT placement in one operation
     * Used by drag-over and allowDrop callbacks to avoid duplication
     * @param dragNode - The node being dragged
     * @param siblings - The current sibling array
     * @param dragIndex - The current index of the drag node
     * @param dropIndex - The target index for the drop
     * @returns validation result with reordered siblings and final index
     *
     * @example
     * const { isValid, reorderedSiblings } = simulateAndValidate(
     *   dragNode,
     *   siblings,
     *   dragIndex,
     *   dropIndex
     * )
     * if (!isValid) {
     *   event.preventDefault()
     *   return
     * }
     */
    simulateAndValidate: (
        dragNode: Partial<ContentItemViewNodesType>,
        siblings: Partial<ContentItemViewNodesType>[],
        dragIndex: number,
        dropIndex: number
    ) => {
        isValid: boolean
        reorderedSiblings: Partial<ContentItemViewNodesType>[]
        finalIndex: number
    }

    /**
     * Helper to check if node is a non-MOPT article
     * Semantic helper for readability
     * @param node - The node to check
     * @returns true if node is an article but not a MOPT article
     *
     * @example
     * if (isNonMOPTArticle(node)) {
     *   // Perform validation for non-MOPT articles
     * }
     */
    isNonMOPTArticle: (node: Partial<ContentItemViewNodesType> | undefined) => boolean
}

/**
 * Hook for consolidating drag-and-drop MOPT validation logic
 *
 * This hook provides a comprehensive set of validation functions for MOPT
 * (Manufacturer-sourced Product Type) restrictions during drag-and-drop operations.
 * It consolidates duplicated logic from useReorderItems.ts into a single,
 * reusable interface.
 *
 * Key Features:
 * - Prevents dragging MOPT articles
 * - Validates placement to prevent non-MOPT after MOPT
 * - Simulates reordering and validates in one operation
 * - Automatically manages dialog state
 *
 * @returns Object containing validation functions
 *
 * @example
 * const {
 *   shouldPreventDrag,
 *   validateDropPlacement,
 *   simulateAndValidate
 * } = useMOPTDragAndDropValidation()
 *
 * // In handleOnDragOver
 * if (shouldPreventDrag(dragNode)) {
 *   event.preventDefault()
 *   return
 * }
 *
 * // Validate placement
 * const { isValid } = simulateAndValidate(dragNode, siblings, dragIndex, dropIndex)
 * if (!isValid) {
 *   event.preventDefault()
 *   return
 * }
 */
export const useMOPTDragAndDropValidation = (): UseMOPTDragAndDropValidationReturn => {
    const { setDialogOpen } = useMOPTDialogActions()

    const shouldPreventDrag = useCallback(
        (node: Partial<ContentItemViewNodesType> | undefined): boolean => {
            // Only articles can be MOPT
            if (!node || node.textType !== 'Article') return false

            // Check if this is a MOPT article
            if (isMOPTArticle(node)) {
                // Show dialog to inform user
                setDialogOpen(true, 'MOVE')
                return true
            }

            return false
        },
        [setDialogOpen]
    )

    const validateDropPlacement = useCallback(
        (
            dragNode: Partial<ContentItemViewNodesType>,
            reorderedSiblings: Partial<ContentItemViewNodesType>[]
        ): boolean => {
            // MOPT articles themselves can't be moved, but we check that elsewhere
            // Non-articles are always allowed
            if (dragNode.textType !== 'Article' || isMOPTArticle(dragNode)) {
                return true
            }

            // Find where the node ended up after reordering
            const finalIndex = reorderedSiblings.findIndex(
                (s) => s.id === dragNode.id
            )

            // Validate MOPT placement rules
            const placementResult = validateMOPTPlacement(
                dragNode,
                reorderedSiblings,
                finalIndex
            )

            if (!placementResult.isValid) {
                // Show dialog to inform user
                setDialogOpen(true, 'MOVE')
                return false
            }

            return true
        },
        [setDialogOpen]
    )

    const simulateAndValidate = useCallback(
        (
            dragNode: Partial<ContentItemViewNodesType>,
            siblings: Partial<ContentItemViewNodesType>[],
            dragIndex: number,
            dropIndex: number
        ) => {
            // Simulate the reordering
            const reorderedSiblings = [...siblings]
            const [movedNode] = reorderedSiblings.splice(dragIndex, 1)
            reorderedSiblings.splice(dropIndex, 0, movedNode)

            // Find final position after reordering
            const finalIndex = reorderedSiblings.findIndex(
                (s) => s.id === dragNode.id
            )

            // Validate the placement
            const isValid = validateDropPlacement(dragNode, reorderedSiblings)

            return {
                isValid,
                reorderedSiblings,
                finalIndex,
            }
        },
        [validateDropPlacement]
    )

    const isNonMOPTArticle = useCallback(
        (node: Partial<ContentItemViewNodesType> | undefined): boolean => {
            return !!(node?.textType === 'Article' && !isMOPTArticle(node))
        },
        []
    )

    return {
        shouldPreventDrag,
        validateDropPlacement,
        simulateAndValidate,
        isNonMOPTArticle,
    }
}
